import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { CopyButton } from '@/components/shared/CopyButton'
import { EmptyState } from '@/components/shared/EmptyState'
import type { Mailbox, Domain } from '@/types'
import { Mail, Plus, Trash2, Loader2, ChevronRight, ChevronLeft } from 'lucide-react'

export function Mailboxes() {
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [addOpen, setAddOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<any>(null)
  const navigate = useNavigate()
  const [form, setForm] = useState({ domain_id: '', local_part: '', display_name: '', password: '', alternate_email: '', quota_mb: 100 })

  const fetchMailboxes = async () => {
    try {
      const [mbRes, dRes] = await Promise.all([
        api.get(`/mailboxes?page=${page}&per_page=20`),
        api.get('/domains'),
      ])
      setMailboxes(mbRes.data.mailboxes || [])
      setTotal(mbRes.data.pagination?.total || 0)
      setTotalPages(mbRes.data.pagination?.total_pages || 1)
      setDomains(dRes.data.domains?.filter((d: Domain) => d.status === 'active') || [])
    } catch {
      toast.error('Failed to load mailboxes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMailboxes()
  }, [page])

  const handleCreate = async () => {
    if (!form.domain_id || !form.local_part) return
    setCreating(true)
    try {
      const res = await api.post('/mailboxes', {
        domain_id: form.domain_id,
        local_part: form.local_part,
        display_name: form.display_name || undefined,
        password: form.password || undefined,
        alternate_email: form.alternate_email || undefined,
        quota_mb: form.quota_mb || undefined,
      })
      setCreated(res.data)
      setForm({ domain_id: '', local_part: '', display_name: '', password: '', alternate_email: '', quota_mb: 100 })
      fetchMailboxes()
    } catch (err: any) {
      const detail = err.response?.data?.detail
      let message = 'Failed to create mailbox'
      if (Array.isArray(detail) && detail.length > 0) {
        message = detail[0].msg || detail[0].message || message
      } else if (typeof detail === 'string') {
        message = detail
      } else if (detail?.message) {
        message = detail.message
      }
      toast.error(message)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this mailbox? It will enter a 7-day grace period.')) return
    try {
      await api.delete(`/mailboxes/${id}`)
      fetchMailboxes()
    } catch {
      toast.error('Failed to delete mailbox')
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="pt-page-header">
        <div>
          <p className="pt-eyebrow">Mailboxes</p>
          <h1 className="pt-page-title mt-2">Your mailboxes.</h1>
          <p className="pt-page-description">Create addresses on your connected domains.</p>
        </div>
        <Button onClick={() => { setCreated(null); setAddOpen(true) }}>
          <Plus className="mr-2 h-4 w-4" />
          Create Mailbox
        </Button>
      </div>

      {mailboxes.length === 0 ? (
        <EmptyState
          title="No mailboxes yet"
          description="Create an address on a connected domain."
          icon={<Mail className="h-5 w-5" />}
          action={<Button onClick={() => setAddOpen(true)}><Plus className="mr-2 h-4 w-4" />Create Mailbox</Button>}
        />
      ) : (
        <div className="grid gap-3">
          {mailboxes.map((mb) => (
            <Card
              key={mb.id}
              className="group cursor-pointer border-border/70 transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:bg-accent/30"
              onClick={() => navigate(`/mailboxes/${mb.id}`)}
            >
              <CardContent className="flex items-center justify-between gap-4 p-5">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-semibold tracking-[-0.015em]">{mb.email}</p>
                      <StatusBadge status={mb.status} />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {mb.domain_name || 'Unknown domain'} · Quota: {mb.quota_used_mb}MB / {mb.quota_mb}MB
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={(e) => { e.stopPropagation(); handleDelete(mb.id); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {mailboxes.length > 0 && (
        <div className="flex items-center justify-between border-t border-border/80 pt-5">
          <p className="text-xs text-muted-foreground">{total} total</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a mailbox</DialogTitle>
          </DialogHeader>
          {!created ? (
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium">Domain</label>
                <select
                  className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.domain_id}
                  onChange={(e) => setForm({ ...form, domain_id: e.target.value })}
                >
                  <option value="">Select domain</option>
                  {domains.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Local Part (before @)</label>
                <Input
                  placeholder="e.g. support"
                  value={form.local_part}
                  onChange={(e) => setForm({ ...form, local_part: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Display Name</label>
                <Input
                  placeholder="e.g. Support Team"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Password (optional - auto-generated if empty)</label>
                <Input
                  type="password"
                  placeholder="Leave empty to auto-generate"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Alternate Email</label>
                <Input
                  type="email"
                  placeholder="For password recovery"
                  value={form.alternate_email}
                  onChange={(e) => setForm({ ...form, alternate_email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Quota (MB)</label>
                <Input
                  type="number"
                  min={10}
                  max={5120}
                  value={form.quota_mb}
                  onChange={(e) => setForm({ ...form, quota_mb: parseInt(e.target.value) || 100 })}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <p className="text-sm text-emerald-400">Mailbox created!</p>
              {created.password && (
                <div className="bg-accent/50 rounded-lg px-3 py-2 flex items-center justify-between gap-2 min-w-0">
                  <span className="text-xs font-mono break-all min-w-0">Password: {created.password}</span>
                  <CopyButton text={created.password} />
                </div>
              )}
              {created.setup_link && (
                <div className="bg-accent/50 rounded-lg px-3 py-2 flex items-center justify-between gap-2 min-w-0">
                  <span className="text-xs font-mono break-all min-w-0">Setup: {created.setup_link}</span>
                  <CopyButton text={created.setup_link} />
                </div>
              )}
              {created.email && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setCreated(null)
                    setAddOpen(false)
                    navigate(`/client-setup?email=${encodeURIComponent(created.email)}`)
                  }}
                >
                  Open Client Setup Guide
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            {!created ? (
              <>
                <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating || !form.domain_id || !form.local_part}>
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create
                </Button>
              </>
            ) : (
              <Button onClick={() => { setCreated(null); setAddOpen(false); }}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
